import { restAxios } from './httpClient';

const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_ATTEMPTS = 60;

const TERMINAL_STATUSES = ['succeeded', 'succeeded_overshot', 'failed', 'canceled', 'skipped', 'suppressed'];

export const getJob = (id) => {
	return restAxios.get(`/jobs/${id}`);
};

const createAbortError = (jobId) => {
	const error = new Error(`Polling for job ${jobId} was aborted.`);
	error.name = 'AbortError';

	return error;
};

export const pollJobUntilTerminal = (jobId, options = {}) => {
	const { intervalMs = DEFAULT_POLL_INTERVAL_MS, maxAttempts = DEFAULT_MAX_ATTEMPTS, signal } = options;

	return new Promise((resolve, reject) => {
		if (!jobId) {
			reject(new Error('Polling for job requires a valid jobId.'));
			return;
		}

		if (signal?.aborted) {
			reject(createAbortError(jobId));
			return;
		}

		let attempts = 0;
		let timeoutId = null;

		const cleanup = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}

			signal?.removeEventListener('abort', onAbort);
		};

		const onAbort = () => {
			cleanup();
			reject(createAbortError(jobId));
		};

		signal?.addEventListener('abort', onAbort);

		const poll = () => {
			attempts += 1;

			restAxios.get(`/jobs/${jobId}`)
				.then((response) => {
					if (signal?.aborted) { return; }

					const status = response?.data?.status?.toLowerCase();

					if (TERMINAL_STATUSES.includes(status)) {
						cleanup();
						resolve(response.data);
					} else if (attempts >= maxAttempts) {
						cleanup();
						reject(new Error(`Polling for job ${jobId} timed out.`));
					} else {
						timeoutId = setTimeout(poll, intervalMs);
					}
				})
				.catch((error) => {
					if (signal?.aborted) { return; }

					if (attempts >= maxAttempts) {
						cleanup();
						reject(error);
					} else {
						timeoutId = setTimeout(poll, intervalMs);
					}
				});
		};

		poll();
	});
};

const SUCCESS_STATUSES = ['succeeded', 'succeeded_overshot'];
const UNAPPLIED_STATUSES = ['skipped', 'suppressed'];

const jobStatus = (job) => {
	return String(job?.status ?? '').toLowerCase();
};

/*
 * `pollJobUntilTerminal` resolves on every terminal status, not only the successful ones - it
 * reports that the job stopped, not that it worked. Treating its resolution as success would
 * announce work that was never done, so the status has to be classified explicitly.
 */
export const isJobSucceeded = (job) => {
	return SUCCESS_STATUSES.includes(jobStatus(job));
};

/*
 * Turns a non-succeeded job into something worth showing.
 *
 * Both messages come from the caller: this module knows what job statuses mean, not what the job
 * was doing. `unappliedMessage` covers the statuses where the job never ran at all, which is a
 * different thing to say than a failure.
 */
export const describeJobOutcome = (job, { failureMessage, unappliedMessage }) => {
	const status = jobStatus(job);

	if (UNAPPLIED_STATUSES.includes(status)) {
		return `${unappliedMessage} (job ${status}).`;
	}

	/*
	 * `statusMessage` is the field this API actually populates - JobSchema carries
	 * `statusMessage`/`errorIdentifier`/`warnings` and has neither `error` nor `message`. The other
	 * two are kept for any caller whose job shape differs; without `statusMessage`, every failed
	 * job reports only its status and throws the cause away.
	 */
	const reported = job?.error ?? job?.message ?? job?.statusMessage;

	if (typeof reported === 'string' && reported.trim()) {
		return reported;
	}

	if (status) {
		return `${failureMessage} (job ${status}).`;
	}

	return failureMessage;
};
